import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Paper,
} from '@mui/material';
import axios from 'axios';

import { API_URL } from '../../constants/urls';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/password-reset/request`,
        null,
        {
          params: { email },
        },
      );

      setSuccess(true);
      // In development, the API returns the token directly
      if (response.data.token) {
        setResetToken(response.data.token);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          'Failed to process request. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" gutterBottom>
              Check Your Email
            </Typography>
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              If an account exists with the email address {email}, we&apos;ve
              sent password reset instructions.
            </Alert>
            {resetToken && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Development Mode:</strong> Your reset token is:{' '}
                  <code>{resetToken}</code>
                  <br />
                  In production, this would be sent via email.
                </Alert>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    navigate(`/reset-password?token=${resetToken}`)
                  }
                  sx={{ mb: 2 }}
                >
                  Continue to Reset Password
                </Button>
              </>
            )}
            <Box textAlign="center">
              <Link component={RouterLink} to="/login" variant="body2">
                Back to Sign In
              </Link>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Forgot Password
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, mb: 3, textAlign: 'center' }}
        >
          Enter your email address and we&apos;ll send you instructions to reset
          your password.
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ mt: 1, width: '100%' }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error}
            helperText={error}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
          <Box textAlign="center">
            <Link component={RouterLink} to="/login" variant="body2">
              Remember your password? Sign In
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
